import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';
import {
  type PricingTier,
  type Addon,
  type OrganizationAddon,
  getTierForUserCount,
  calculateMonthlyPrice,
  calculateTotalWithAddons,
  getUserTierLabel,
} from '../utils/pricingUtils';

export interface SubscriptionPlan {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
  max_users: number;
  max_modules: number;
  features: string[];
  trial_days: number;
}

export interface Organization {
  id: string;
  name: string;
  subscription_plan_id: string;
  subscription_status: 'trial' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'cancelling' | 'expired';
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_test_mode: boolean;
  billing_period: 'monthly' | 'yearly' | null;
  last_payment_at: string | null;
  grace_period_ends_at: string | null;
  subscription_seats: number | null;
  active: boolean;
  /** 'reseller' = l'abbonamento e' del partner rivenditore, non di questa org. */
  billing_model: 'direct' | 'reseller' | null;
  subscription_plan?: SubscriptionPlan;
}

export interface SubscriptionStatus {
  organization: Organization | null;
  plan: SubscriptionPlan | null;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  isSubscriptionActive: boolean;
  trialDaysRemaining: number;
  subscriptionExpiry: Date | null;
  canAccessModule: (module: string) => boolean;
  canAddUser: () => boolean;
  userCount: number;
  maxUsers: number;
  loading: boolean;
  error: string | null;
  // Campi Stripe
  billingPeriod: 'monthly' | 'yearly' | null;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  lastPaymentAt: Date | null;
  gracePeriodEndsAt: Date | null;
  subscriptionSeats: number | null;
  isPastDue: boolean;
  isInGracePeriod: boolean;
  /** L'abbonamento e' gestito da un partner rivenditore: niente self-service. */
  isBilledByReseller: boolean;
  hasActiveStripeSubscription: boolean;
  invalidate: () => void;
  setOrganizationStatus: (status: string, endsAt?: string) => void;
  // Pricing tiers & addons
  currentTier: PricingTier | null;
  currentTierLabel: string;
  activeAddons: OrganizationAddon[];
  baseMonthlyPrice: number;
  totalMonthlyFee: number;
}

// L'hook è montato da più componenti in parallelo (banner, layout, guardie
// moduli): senza una cache condivisa ogni mount rifarebbe l'intera catena
// organizations + conteggio utenti + tiers + addons.
interface SubscriptionData {
  organization: Organization;
  plan: SubscriptionPlan | null;
  userCount: number;
  currentTier: PricingTier | null;
  activeAddons: OrganizationAddon[];
}
const subscriptionCache = new Map<string, { promise: Promise<SubscriptionData>; ts: number }>();
const SUBSCRIPTION_FETCH_TTL = 60 * 1000;

function loadSubscriptionData(organizationId: string, force = false): Promise<SubscriptionData> {
  const cached = subscriptionCache.get(organizationId);
  if (!force && cached && Date.now() - cached.ts < SUBSCRIPTION_FETCH_TTL) {
    return cached.promise;
  }
  const promise = (async (): Promise<SubscriptionData> => {
    // Fetch organization with subscription plan
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select(`
        *,
        subscription_plan:subscription_plans(*)
      `)
      .eq('id', organizationId)
      .single();

    if (orgError) {
      throw new Error('Errore nel recuperare i dati dell\'organizzazione');
    }

    // Fetch user count (excluding support users)
    const { count, error: userCountError } = await supabase
      .from('user_profiles')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId)
      .eq('active', true)
      .not('email', 'like', '%@obly.it')
      .not('email', 'like', '%@flusia.it');

    if (userCountError) {
      console.error('Error fetching user count:', userCountError);
    }

    // Fetch pricing tier for this org
    let currentTier: PricingTier | null = null;
    const activeUserCount = count || 1;
    if (orgData.subscription_plan_id) {
      const { data: tiers } = await supabase
        .from('subscription_pricing_tiers')
        .select('*')
        .eq('plan_id', orgData.subscription_plan_id)
        .order('min_users');

      if (tiers && tiers.length > 0) {
        currentTier = getTierForUserCount(tiers as PricingTier[], activeUserCount);
      }
    }

    // Fetch active addons
    const { data: orgAddons } = await supabase
      .from('organization_addons')
      .select('*, addon:subscription_addons(*)')
      .eq('organization_id', organizationId)
      .eq('status', 'active');

    return {
      organization: orgData as Organization,
      plan: (orgData.subscription_plan as SubscriptionPlan) || null,
      userCount: count || 0,
      currentTier,
      activeAddons: (orgAddons as OrganizationAddon[]) || [],
    };
  })();
  subscriptionCache.set(organizationId, { promise, ts: Date.now() });
  promise.catch(() => subscriptionCache.delete(organizationId));
  return promise;
}

export function useSubscription(): SubscriptionStatus {
  const { user, userProfile } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = React.useRef(false);
  const organizationIdRef = React.useRef<string | null>(null);

  // Pricing tiers & addons
  const [currentTier, setCurrentTier] = useState<PricingTier | null>(null);
  const [activeAddons, setActiveAddons] = useState<OrganizationAddon[]>([]);

  const fetchSubscriptionData = useCallback(async () => {
    if (!userProfile?.organization_id) {
      return;
    }

    // Check if we already fetched for this organization
    if (hasFetchedRef.current && organizationIdRef.current === userProfile.organization_id) {
      return;
    }

    try {
      organizationIdRef.current = userProfile.organization_id;
      hasFetchedRef.current = true;
      setLoading(true);
      setError(null);

      const data = await loadSubscriptionData(userProfile.organization_id);

      setOrganization(data.organization);
      setPlan(data.plan);
      setUserCount(data.userCount);
      setCurrentTier(data.currentTier);
      setActiveAddons(data.activeAddons);

    } catch (err) {
      console.error('Subscription data fetch error:', err);
      setError(err instanceof Error ? err.message : 'Errore nel recuperare i dati dell\'abbonamento');
      // Reset on error so user can retry
      hasFetchedRef.current = false;
      organizationIdRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [userProfile?.organization_id]);

  useEffect(() => {
    // Only fetch when we have both user and organization_id
    if (user?.id && userProfile?.organization_id) {
      fetchSubscriptionData();
    } else if (!user?.id || !userProfile?.organization_id) {
      setLoading(false);
    }
  }, [user?.id, userProfile?.organization_id, fetchSubscriptionData]);

  // Organizzazione fatturata a un partner rivenditore: l'abbonamento e' del
  // partner. Niente banner di insoluto, niente scadenza trial, niente upsell:
  // il cliente paga regolarmente, solo non a Flusia direttamente.
  const isBilledByReseller = organization?.billing_model === 'reseller';

  const isTrialActive = (): boolean => {
    if (isBilledByReseller) return false;
    if (!organization || organization.subscription_status !== 'trial') return false;
    if (!organization.trial_ends_at) return false;

    const trialEnd = new Date(organization.trial_ends_at);
    return trialEnd > new Date();
  };

  const isTrialExpired = (): boolean => {
    if (isBilledByReseller) return false;
    if (!organization || organization.subscription_status !== 'trial') return false;
    if (!organization.trial_ends_at) return false;

    const trialEnd = new Date(organization.trial_ends_at);
    return trialEnd <= new Date();
  };

  const isSubscriptionActive = (): boolean => {
    if (!organization) return false;

    // Le organizzazioni del rivenditore sono attive finche' il partner paga, e
    // questo si riflette su organizations.active (gestito dal cron
    // partner-reseller-archive-due). Se l'organizzazione e' active, i moduli
    // devono funzionare: senza questo ramo canAccessModule() bloccherebbe tutto
    // per un'org reseller con subscription_status residuo 'trial'.
    if (isBilledByReseller) return organization.active === true;

    // Trial is considered active subscription
    if (isTrialActive()) return true;

    if (!organization.active) return false;
    const status = organization.subscription_status;
    if (status === 'active') return true;
    // In disdetta: ha pagato fino a fine periodo, tutto resta attivo fino ad allora.
    if (status === 'cancelling') return true;
    // Pagamento non riuscito: fino alla sospensione (fine dei 14 giorni) il cliente
    // lavora con tutto, Magazzino e Marketplace compresi, come dice la mail (26/09/2026).
    if (status === 'past_due' && organization.grace_period_ends_at) {
      return new Date(organization.grace_period_ends_at) > new Date();
    }
    return false;
  };

  const getTrialDaysRemaining = (): number => {
    if (!organization || !organization.trial_ends_at || organization.subscription_status !== 'trial') {
      return 0;
    }
    
    const trialEnd = new Date(organization.trial_ends_at);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const getSubscriptionExpiry = (): Date | null => {
    if (!organization) return null;
    
    if (organization.subscription_status === 'trial' && organization.trial_ends_at) {
      return new Date(organization.trial_ends_at);
    }
    
    if (organization.subscription_ends_at) {
      return new Date(organization.subscription_ends_at);
    }
    
    return null;
  };

  const canAccessModule = (module: string): boolean => {
    if (!isSubscriptionActive()) return false;

    // Trial senza piano assegnato: accesso completo
    if (!plan) {
      if (organization?.subscription_status === 'trial') return true;
      return false;
    }

    // If plan allows unlimited modules (-1), allow access
    if (plan.max_modules === -1) return true;

    // Check if module is enabled in the plan's enabled_modules
    const planModules = (plan as any).enabled_modules as string[] | undefined;
    if (planModules && planModules.length > 0) {
      if (planModules.includes(module)) return true;
    } else {
      // enabled_modules non configurato — blocca e logga warning
      console.warn(`[useSubscription] Piano "${plan.name}" non ha enabled_modules configurato. Modulo "${module}" bloccato.`);
      return false;
    }

    // Check if any active addon unlocks this module
    if (activeAddons.length > 0) {
      const unlockedByAddon = activeAddons.some(oa => {
        const addon = (oa as any).addon as Addon | undefined;
        return addon?.unlocks_modules?.includes(module);
      });
      if (unlockedByAddon) return true;
    }

    // Module not in plan and not unlocked by addon
    return false;
  };

  const canAddUser = (): boolean => {
    if (!isSubscriptionActive()) return false;
    if (!plan) return false;

    // If plan allows unlimited users (-1), allow addition
    if (plan.max_users === -1) return true;

    // If plan has tiered pricing, users can always be added (they just pay more)
    if (currentTier) return true;

    return userCount < plan.max_users;
  };

  // Funzioni helper per i nuovi campi Stripe
  const isPastDue = (): boolean => {
    if (isBilledByReseller) return false;
    return organization?.subscription_status === 'past_due';
  };

  const isInGracePeriod = (): boolean => {
    if (isBilledByReseller) return false;
    if (!organization?.grace_period_ends_at) return false;
    return new Date(organization.grace_period_ends_at) > new Date();
  };

  const hasActiveStripeSubscription = (): boolean => {
    return !!(organization?.stripe_subscription_id &&
      (organization?.subscription_status === 'active' || organization?.subscription_status === 'cancelling'));
  };

  // Force refetch of subscription data
  const invalidate = useCallback(() => {
    if (organizationIdRef.current) {
      subscriptionCache.delete(organizationIdRef.current);
    }
    hasFetchedRef.current = false;
    organizationIdRef.current = null;
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  // Optimistic update of organization status (for immediate UI feedback)
  const setOrganizationStatus = useCallback((status: string, endsAt?: string) => {
    setOrganization(prev => prev ? {
      ...prev,
      subscription_status: status as Organization['subscription_status'],
      subscription_ends_at: endsAt || prev.subscription_ends_at,
    } : null);
  }, []);

  return {
    organization,
    plan,
    isTrialActive: isTrialActive(),
    isTrialExpired: isTrialExpired(),
    isSubscriptionActive: isSubscriptionActive(),
    trialDaysRemaining: getTrialDaysRemaining(),
    subscriptionExpiry: getSubscriptionExpiry(),
    canAccessModule,
    canAddUser,
    userCount,
    maxUsers: plan?.max_users || 0,
    loading,
    error,
    // Campi Stripe
    billingPeriod: organization?.billing_period || null,
    stripeSubscriptionId: organization?.stripe_subscription_id || null,
    stripeCustomerId: organization?.stripe_customer_id || null,
    lastPaymentAt: organization?.last_payment_at ? new Date(organization.last_payment_at) : null,
    gracePeriodEndsAt: organization?.grace_period_ends_at ? new Date(organization.grace_period_ends_at) : null,
    subscriptionSeats: organization?.subscription_seats || null,
    isPastDue: isPastDue(),
    isInGracePeriod: isInGracePeriod(),
    isBilledByReseller,
    hasActiveStripeSubscription: hasActiveStripeSubscription(),
    invalidate,
    setOrganizationStatus,
    // Pricing tiers & addons
    currentTier,
    currentTierLabel: getUserTierLabel(userCount || 1),
    activeAddons,
    baseMonthlyPrice: currentTier
      ? calculateMonthlyPrice(currentTier, userCount || 1, organization?.billing_period === 'yearly')
      : (plan?.price_monthly || 0),
    totalMonthlyFee: currentTier
      ? calculateTotalWithAddons(
          calculateMonthlyPrice(currentTier, userCount || 1, organization?.billing_period === 'yearly'),
          activeAddons.map(a => (a as any).addon).filter(Boolean)
        )
      : (plan?.price_monthly || 0),
  };
}